import AnnouncementFilterField from "@mapmycustomers/shared/enum/announcements/AnnouncementFilterField";
import AnnouncementPageId from "@mapmycustomers/shared/enum/announcements/AnnouncementPageId";
import {
  createEqualityFieldSpec,
  EqualityValueKind,
  FilterSchema,
  integerFieldSpec,
  stringFieldSpec,
  stringSetFieldSpec,
  versionFieldSpec,
} from "@mapmycustomers/shared/util/filterMatcher";

import AnnouncementFilterContext from "@app/types/AnnouncementFilterContext";

import { isKnownPageId, matchesPage } from "./pageRegistry";

// Web cannot evaluate the two mobile-only fields, so a filter using one is rejected as unknown.
export type WebAnnouncementFilterField = Exclude<
  AnnouncementFilterField,
  AnnouncementFilterField.CODE_PUSH_VERSION | AnnouncementFilterField.PLATFORM
>;

// A page id must be in the registry, and a section also matches its sub-pages.
const PAGE_ID_KIND: EqualityValueKind<AnnouncementPageId> = {
  equals: matchesPage,
  isValue: (value): value is AnnouncementPageId =>
    typeof value === "string" && isKnownPageId(value),
};

// Most add-ons are flags; an object-valued one such as healthcareData carries an enabled field.
const isAddOnEnabled = (value: unknown): boolean =>
  typeof value === "object" && value !== null
    ? "enabled" in value && Boolean(value.enabled)
    : Boolean(value);

const getEnabledKeys = (addOns: Partial<Record<string, unknown>>): string[] =>
  Object.keys(addOns).filter((key) => isAddOnEnabled(addOns[key]));

const announcementFilterSchema: FilterSchema<
  AnnouncementFilterContext,
  WebAnnouncementFilterField
> = {
  [AnnouncementFilterField.APP_VERSION]: versionFieldSpec({
    getValue: (filterContext) => filterContext.appVersion,
  }),
  [AnnouncementFilterField.ORG_ADD_ONS]: stringSetFieldSpec({
    getValue: (filterContext) => getEnabledKeys(filterContext.orgAddOns),
  }),
  [AnnouncementFilterField.ORG_ID]: integerFieldSpec({
    getValue: (filterContext) => filterContext.orgId,
  }),
  [AnnouncementFilterField.PAGE_ID]: createEqualityFieldSpec(PAGE_ID_KIND, {
    getValue: (filterContext) => filterContext.pageId,
  }),
  [AnnouncementFilterField.ROLE_KEY]: stringFieldSpec({
    getValue: (filterContext) => filterContext.roleKey,
  }),
  [AnnouncementFilterField.USER_ADD_ONS]: stringSetFieldSpec({
    getValue: (filterContext) => getEnabledKeys(filterContext.userAddOns),
  }),
  [AnnouncementFilterField.USER_ID]: integerFieldSpec({
    getValue: (filterContext) => filterContext.userId,
  }),
};

export default announcementFilterSchema;
